import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FormattedMessage, useIntl } from "react-intl";
import { useIonToast } from "@ionic/react";
import { lazy, Suspense } from "react";
import { IonSpinner, isPlatform } from "@ionic/react";
import AiApi from "../../../api/ai";
import SkeletonText from "../../SkeletonText";
import { IconInfoCircle, IconBulb, IconList, IconTypography } from "@tabler/icons-react";

const TextEditor = lazy(() => import("../../common/text-editor"));

import styles from "./styles.module.css";

const isMobile = isPlatform("android") || isPlatform("ios");

export default function FreeText() {
  const queryClient = useQueryClient();
  const intl = useIntl();
  const [present] = useIonToast();
  const { data: statusData } = useQuery({
    queryFn: () => AiApi.getAiStatus(),
    queryKey: ["getAiStatus"],
    refetchOnWindowFocus: false,
    staleTime: 300000,
  });

  const {
    data: aiData,
    isLoading: isGettingAi,
    isRefetching: isRefetchingAi,
  } = useQuery({
    queryFn: () => AiApi.getFreeText(),
    queryKey: ["getAiFreeText"],
    refetchOnWindowFocus: false,
  });

  const refreshData = async (embededContent?: string) => {
    const queriesToInvalidate = ["getAiFreeText"];

    if (!statusData?.status?.text || !embededContent?.trim()) {
      queriesToInvalidate.push("getAiStatus");
    }

    await Promise.all(
      queriesToInvalidate.map((queryKey) =>
        queryClient.invalidateQueries({ queryKey: [queryKey] })
      )
    );
  };

  const mutateUpdateText = useMutation({
    mutationFn: AiApi.createUpdateFreeText,
    onSuccess: async (_, variables) => {
      await refreshData(variables.embededContent);

      return present({
        message: intl.formatMessage({ id: "aiUpdateSucces" }),
        duration: 4000,
        color: "light",
        position: "top",
        cssClass: "success-toast",
      });
    },
    onError: () => {
      return present({
        message: intl.formatMessage({ id: "common.tryItLater" }),
        duration: 5000,
        color: "light",
        position: "top",
        cssClass: "error-toast",
      });
    },
  });

  const isRequesting =
    isGettingAi || isRefetchingAi || mutateUpdateText.isPending;
  const textEmbedding = aiData?.embedding || null;

  return isRequesting ? (
    isMobile ? (
      <div className="p-3 flex-align-center justify-content-center">
        <IonSpinner name="lines-sharp"></IonSpinner>
      </div>
    ) : (
      <SkeletonText rows={10} />
    )
  ) : (
    <Suspense fallback={<SkeletonText rows={10} />}>
      {!textEmbedding && isMobile && (
        <div className={isMobile ? "view-pane" : undefined}>
          <div className="visito-card light">
            <div className="visito-card-body">
              <div className="flex flex-row gap-3 align-items-center color-step-500 fs-2">
                <div>
                  <IconInfoCircle size={20} />
                </div>
                <span style={{ whiteSpace: "pre-line" }}>
                  <b>
                    <FormattedMessage id="ai.sources.text.tip.title" />
                  </b>
                  <br />
                  <FormattedMessage id="ai.sources.text.tip.examples" />
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="flex-body-wrapper">
        <div className="flex w-100 h-100 gap-3">
          <div className={`h-100 d-flex flex-grow-1 flex-column gap-2 ${isMobile ? "w-100" : "w-75"}`}>
            <TextEditor
              text={textEmbedding?.embededContent || null}
              onSave={(embededContent) =>
                mutateUpdateText.mutate({
                  embededContent,
                  embeddingId: textEmbedding?._id,
                  updateTrain: !statusData?.status?.trained,
                })
              }
            />
          </div>

          {!isMobile && (
            <aside className="h-100" style={{ width: '25%', minWidth: '300px' }}>
              <div className={styles.helpCard}>
                <div className={styles.helpHeader}>
                  <IconBulb size={20} className={styles.helpIcon} />
                  <h3><FormattedMessage id="ai.textHelp.title" /></h3>
                </div>

                <div className={styles.helpBody}>
                  <p className={styles.introText}>
                    <FormattedMessage id="ai.textHelp.intro" />
                  </p>

                  <ul className={styles.helpList}>
                    <li>
                      <div className={styles.listIconBox}><IconList size={16}/></div>
                      <div>
                        <strong><FormattedMessage id="ai.textHelp.faqs.title" /></strong>
                        <span><FormattedMessage id="ai.textHelp.faqs.desc" /></span>
                      </div>
                    </li>
                    
                    <li>
                      <div className={styles.listIconBox}>$</div>
                      <div>
                        <strong><FormattedMessage id="ai.textHelp.specific.title" /></strong>
                        <span><FormattedMessage id="ai.textHelp.specific.desc" /></span>
                      </div>
                    </li>

                    <li>
                      <div className={styles.listIconBox}><IconTypography size={16}/></div>
                      <div>
                        <strong><FormattedMessage id="ai.textHelp.format.title" /></strong>
                        <span><FormattedMessage id="ai.textHelp.format.desc" /></span>
                      </div>
                    </li>
                  </ul>

                  <div className={styles.proTip}>
                    <FormattedMessage id="ai.textHelp.tip" />
                  </div>
                </div>

              </div>
            </aside>
          )}
        </div>

      </div>
    </Suspense>
  );
}
